import { AnimatedSpinnerGapIcon } from '@monorepo/shared/componentsV2/animation/AnimatedSpinnerGapIcon';
import { CalculationErrorChip } from '@monorepo/shared/componentsV2/errorDisplays/CalculationErrorChip';
import { Link } from '@monorepo/shared/componentsV2/Link';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { Table } from '@monorepo/shared/componentsV2/Table/Table';
import {
  RowStatus,
  TableColumn,
} from '@monorepo/shared/componentsV2/Table/types';
import { Tooltip } from '@monorepo/shared/componentsV2/Tooltip';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { useToast } from '@monorepo/shared/contexts';
import { useDynamicLinkOnSameProject } from '@monorepo/shared/hooks/navigation/useLinkOnSame/useLinkOnSameProject';
import { useHasProjectSettingsPermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { usePrevious } from '@monorepo/shared/hooks/usePrevious';
import { useDatasets } from '@monorepo/shared/hooks/datasets/useDatasets';
import { lookFor } from '@monorepo/shared/utils/lookFor';
import { DatasetChip } from '@monorepo/workflowDataSetup/src/components/shared/DatasetChip';
import { DatasetType, EdpViewRefResponse, ViewType } from 'mapistry-shared';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { TruncatedLabel } from '@monorepo/shared/componentsV2/TruncatedLabel';
import { ViewsTableActions } from '../ViewsTableActions';

const DataSourceContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const StyledDatasetChip = styled(DatasetChip)`
  margin: 0.5rem 0;
`;

const StaleViewTooltip = styled(Tooltip)`
  & svg {
    width: 0.875rem;
    height: 0.875rem;
    fill: ${({ theme }) => theme.colors.orange};
  }
`;

const StyledTruncatedLabel = styled(TruncatedLabel)`
  width: fit-content;
  max-width: 28vw;
`;

interface ViewsTableProps {
  filter: string;
  views?: EdpViewRefResponse[];
  isLoading: boolean;
}

function getCalculationErrorTooltip({
  doesUpstreamUseIncompleteData,
  doesUseIncompleteData,
}: EdpViewRefResponse) {
  if (doesUpstreamUseIncompleteData) {
    return 'This source contains a calculation error.';
  }
  if (doesUseIncompleteData) {
    return 'This Report contains a calculation error.';
  }
  return undefined;
}

const originStatusColumn = {
  id: 'origin-status',
  header: '',
  width: '2%',
  contents: ({ originStatus }: EdpViewRefResponse) => {
    if (originStatus?.isStale) {
      const staleMessage =
        'The data source for this Report has been recently updated and data may not be accurate until recalculation completes.';
      return (
        <>
          <VisuallyHidden>{staleMessage}</VisuallyHidden>
          <StaleViewTooltip title={staleMessage}>
            <span>
              <AnimatedSpinnerGapIcon />
            </span>
          </StaleViewTooltip>
        </>
      );
    }
    return (
      <VisuallyHidden>
        The data source for this Report is up-to-date.
      </VisuallyHidden>
    );
  },
  padding: 'none' as const,
};

function useGetViewsTableColumns({
  areAnyViewsStale,
  canEditViews,
}: {
  areAnyViewsStale: boolean;
  canEditViews: boolean;
}) {
  const { organizationId } = useParams();

  const { getLinkOnSameProject } = useDynamicLinkOnSameProject();
  const { datasets } = useDatasets({ organizationId });

  const createViewNameColumnContent = useCallback(
    ({ id, name }) => (
      <Link to={getLinkOnSameProject(`/logs/views/${id}`)} color="grey">
        {name}
      </Link>
    ),
    [getLinkOnSameProject],
  );

  const createActionsColumnContent = useCallback(
    (view: EdpViewRefResponse) => (
      <ViewsTableActions
        viewId={view.id}
        viewName={view.name}
        viewOriginId={view.originId}
        viewType={view.type}
      />
    ),
    [],
  );

  const columns: TableColumn<EdpViewRefResponse>[] = useMemo(
    () => [
      ...(areAnyViewsStale ? [originStatusColumn] : []),
      {
        id: 'view-name',
        header: 'Report Name',
        width: '35%',
        contents: createViewNameColumnContent,
      },
      {
        id: 'data-sources',
        header: 'Data source',
        width: '25%',
        contents: (view: EdpViewRefResponse) => {
          const { originName, originId, type } = view;
          const datasetType =
            type === ViewType.LOG
              ? DatasetType.LOG
              : datasets?.find((d) => d.id === originId)?.type;
          const calculationErrorTooltip = getCalculationErrorTooltip(view);
          return (
            <DataSourceContainer>
              <StyledDatasetChip name={originName} type={datasetType} />
              {calculationErrorTooltip && (
                <CalculationErrorChip tooltip={calculationErrorTooltip} />
              )}
            </DataSourceContainer>
          );
        },
      },
      {
        id: 'description',
        header: 'Description',
        width: '20%',
        contents: ({ description }) =>
          description ? <StyledTruncatedLabel label={description} /> : '',
      },
      {
        id: 'limits',
        header: 'Limits',
        width: '10%',
        contents: 'limitQueryStepCount',
      },
      ...(canEditViews
        ? [
            {
              id: 'actions',
              header: 'Actions',
              contents: createActionsColumnContent,
              align: 'center' as const,
              width: '10%',
            },
          ]
        : []),
    ],
    [
      areAnyViewsStale,
      canEditViews,
      createActionsColumnContent,
      createViewNameColumnContent,
      datasets,
    ],
  );

  return columns;
}

export function ViewsTable({ filter, views, isLoading }: ViewsTableProps) {
  const { projectId } = useParams();
  const canEditViews = useHasProjectSettingsPermissions(projectId);

  const filterViewsBy = useCallback(
    (view) => lookFor(filter, view, ['name', 'originName', 'description']),
    [filter],
  );

  const numStaleViews = useMemo(
    () => views?.filter((view) => view.originStatus?.isStale).length ?? 0,
    [views],
  );
  const previousNumStaleViews = usePrevious(numStaleViews);

  const columns = useGetViewsTableColumns({
    canEditViews,
    areAnyViewsStale: numStaleViews > 0,
  });

  const { success } = useToast();

  useEffect(() => {
    if (previousNumStaleViews === undefined) return;
    if (previousNumStaleViews <= numStaleViews) return;

    success(
      numStaleViews > 0
        ? 'Some Reports that were recalculating are ready.'
        : 'All Reports are finished recalculating.',
    );
  }, [previousNumStaleViews, numStaleViews, success]);

  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <SkipLink linkText="Skip past Reports table" />
      <Table
        ariaLabel="All Reports"
        columns={columns}
        rowFilter={filterViewsBy}
        rows={
          views?.map((view) => ({
            ...view,
            rowStatus: view.originStatus?.isStale
              ? RowStatus.WARNING
              : undefined,
          })) || []
        }
      />
      <SkipLinkTarget />
    </>
  );
}
